/**
 * Link — internal anchor over Deep-Linked State (route.ts): the href carries
 * the full deep-linked URL (middle-click/new-tab work), and same-app clicks
 * are intercepted for history-based navigation with modifier-key semantics
 * preserved.
 */

import type { MouseEvent } from 'react'
import type { PeriodSelection } from '@domain/types'
import { hrefFor, useNavigate } from './route'

export function Link({
  to,
  className,
  testid,
  period,
  children,
  ariaLabel,
}: {
  to: string
  className?: string
  testid?: string
  /** The current view's period, carried into the target URL (Deep-Linked State). */
  period: PeriodSelection
  /** Optional accessible name override (UX-T16 §structural 4: chip pattern is `{Status} — {count} invoices for {deployment}`). */
  ariaLabel?: string
  children: React.ReactNode
}) {
  const navigate = useNavigate()
  const onClick = (event: MouseEvent<HTMLAnchorElement>) => navigate(event, to, period)

  return (
    <a
      href={hrefFor(to, period)}
      className={className}
      data-testid={testid}
      onClick={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </a>
  )
}
